import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { Observable } from 'rxjs';
import { ServiceBase } from './service-base';
import { RoleDto } from '@planetact/models/role-dto';
import { UserLocal } from '@planetact/models/user-local';
import { ROLE_KEY, TOKEN_KEY } from '@planetact/shared/configToken';
import { ContributorsDto } from '@planetact/models/contributors-dto';
import { Role } from '@planetact/shared/enums/enums';
import { UserDto } from '@planetact/models';
import { CreatedByContributorUserDto } from '@planetact/models/item-details-dto';

const USER_KEY = 'auth-user';

@Injectable({
  providedIn: 'root',
})
export class UserService extends ServiceBase {
  private controllerRoute = `${this.baseRoute}/User`;

  constructor(
    httpClient: HttpClient,
    private notification: NzNotificationService
  ) {
    super(httpClient, notification);
  }

  getAll(): Observable<UserDto[]> {
    const url = `${this.controllerRoute}/getall`;
    return this.httpClient.get<UserDto[]>(url);
  }

  add(user: UserDto | null): Observable<UserDto> {
    const url = `${this.controllerRoute}/create`;
    return this.httpClient.post<UserDto>(url, user);
  }

  update(user: UserDto): Observable<UserDto> {
    const url = `${this.controllerRoute}/update`;
    return this.httpClient.patch<UserDto>(url, user);
  }

  getUserRole(): Observable<RoleDto> {
    const url = `${this.controllerRoute}/role`;
    return this.httpClient.get<RoleDto>(url);
  }

  deleteById(userId: string): Observable<UserDto> {
    const url = `${this.controllerRoute}/delete/${userId}`;
    return this.httpClient.delete<UserDto>(url);
  }

  findUsersByKeyword(keyword: string): Observable<ContributorsDto[]> {
    const url = `${this.controllerRoute}/contributors?keyword=${keyword}`;
    return this.httpClient.post<ContributorsDto[]>(url, null);
  }

  findUsersByKeywordFromAzureAd(keyword: string): Observable<UserDto[]> {
    const url = `${this.controllerRoute}/search?keyword=${keyword}`;
    return this.httpClient.get<UserDto[]>(url);
  }

  getLocalUser(): UserLocal | null {
    const user = localStorage.getItem(ROLE_KEY);
    return user ? (JSON.parse(user) as UserLocal) : null;
  }

  getLocalUserRole(): RoleDto | null {
    const user = localStorage.getItem(ROLE_KEY);
    return user ? (JSON.parse(user) as RoleDto) : null;
  }

  isAuthenticated(): boolean {
    return this.getLocalUser() !== null;
  }

  generateAvatar(): string | null {
    const user = this.getLocalUser();
    if (user && user.firstName && user.lastName) {
      return `${user.firstName.charAt(0)}${user.lastName.charAt(
        0
      )}`.toUpperCase();
    }
    return null;
  }

  generateAvatarText(user: CreatedByContributorUserDto | undefined): string | null {
    if (user && user.firstName && user.lastName) {
      return `${user.firstName.charAt(0)}${user.lastName.charAt(
        0
      )}`.toUpperCase();
    }
    return null;
  }

  logout(): void {
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(TOKEN_KEY);
  }

  isContributor(listId: string[]): boolean {
    const user = this.getLocalUser();
    if (!user) return false;

    if (Role.Admin.toString() == user.roleName) {
      return true;
    }

    if (user.id && listId.includes(user.id)) {
      return true;
    }
    return false;
  }

  isTokenExpired(): boolean {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return true;

    const payload =
      token.split('.')[1] +
      '=='.substring(0, (3 - (token.split('.')[1].length % 3)) % 3);
    const claims = JSON.parse(window.atob(payload));
    const expirationDate = new Date(claims.exp * 1000);
    return expirationDate < new Date();
  }

  isAdmin(): boolean {
    const user = this.getLocalUser();
    if (!user) return false;
    return Role.Admin.toString() == user.roleName;
  }

  getCurrentUser(): UserLocal | null {
    const user = localStorage.getItem(USER_KEY);
    return user ? (JSON.parse(user) as UserLocal) : null;
  }

  getUserAsContributor(): ContributorsDto {
    const user = this.getLocalUser();
    let isOwner = false;
    if(user?.roleName == Role.Admin.toString()) isOwner = true;
    return new ContributorsDto({
      id: user!.id,
      fullName: user!.fullName ?? '',
      isOwner: isOwner
    });
  }
}
